import { Client } from "@/Common/Api/Client";
import { PinFrequencyDocumentType } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IChangePINRequest } from "./Dto/IChangePINRequest";
import { IChangePINResponse } from "./Dto/IChangePINResponse";
import { ILoadSignatureByteArrayResponse } from "./Dto/ILoadSignatureByteArrayResponse";
import { IRefreshSignaturePinTokenResponse } from "./Dto/IRefreshSignaturePinTokenResponse";
import { IRemoveSignatureImageResponse } from "./Dto/IRemoveSignatureImageResponse";
import { ISaveLoginPinFrequencyRequest } from "./Dto/ISaveLoginPinFrequencyRequest";
import { ISaveLoginPinFrequencyResponse } from "./Dto/ISaveLoginPinFrequencyResponse";
import { IValidatePINRequest } from "./Dto/IValidatePINRequest";
import { IValidatePinResponse } from "./Dto/IValidatePINResponse";
import { IValidateSignaturePinTokenResponse } from "./Dto/IValidateSignaturePinTokenResponse";

class SignatureClient {
    /**
     * Get the signature byte array of the current user as a base64-encoded string.
     */
    public static async loadSignatureByteArray(): Promise<string> {
        const response = await Client.postAsync<ILoadSignatureByteArrayResponse>(
            ServerOperations.Server.Myle_Settings_LoadSignatureByteArray,
            {}
        );

        if (response?.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return "";
        }

        return response.data.SignatureBase64;
    }

    /**
     * Save the login PIN frequency for a user.
     * @param request The PIN frequency model to save.
     * @returns The updated PIN frequency data from the server.
     */
    public static async saveLoginPinFrequency(request: ISaveLoginPinFrequencyRequest): Promise<ISaveLoginPinFrequencyResponse> {
        const response = await Client.postAsync<ISaveLoginPinFrequencyResponse>(
            ServerOperations.Server.Myle_Settings_SaveLoginPinFrequency,
            request
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     * Save a signature image from a given file path and get the base64 of the signature image.
     * @param path The file path of the image on the server.
     * @returns The base64-encoded signature image, or null if it failed to save.
     */
    public static async saveSignatureByPath(path: string): Promise<string | null> {
        const response = await Client.postAsync<{ SignatureBase64: string }>(
            ServerOperations.Server.Myle_Settings_SaveSignFileName,
            {
                Path: path
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data.SignatureBase64;
    }

    /**
     * Set a user's new PIN.
     * @param request The PIN change request.
     * @returns Whether the PIN was changed successfully.
     */
    public static async changePin(request: IChangePINRequest): Promise<boolean> {
        const response = await Client.postAsync<IChangePINResponse>(
            ServerOperations.Server.Configuration_PINChange,
            request
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        return response.data.Success;
    }

    /**
     * Validate a PIN entered by a user.
     * @param request The PIN validation request.
     * @returns Whether the provided PIN is valid.
     */
    public static async validatePin(request: IValidatePINRequest): Promise<boolean> {
        const response = await Client.postAsync<IValidatePinResponse>(
            ServerOperations.Server.Configuration_Validate_PIN,
            request
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        return response.data.Valid;
    }

    /**
     * Remove the signature image associated with a user.
     * @param employeeContactId The user's contact ID.
     * @returns An empty string if the signature image was successfully removed, else null.
     */
    public static async removeSignatureImage(employeeContactId: string): Promise<boolean> {
        const response = await Client.postAsync<IRemoveSignatureImageResponse>(
            ServerOperations.Server.Configuration_RemoveSignatureImage,
            {
                EmployeeId: employeeContactId
            }
        );

        if (response.errors?.any())
            sessionStore.showPopup(response.errors);

        return response.data.Success;
    }

    /**
     * Validate a PIN frequency token.
     * @param token The token to validate.
     * @returns Whether the token is valid.
     */
    public static async validatePinFrequencyToken(token: string): Promise<boolean> {
        const response = await Client.postAsync<IValidateSignaturePinTokenResponse>(
            ServerOperations.Server.Myle_Settings_ValidateSignaturePinToken,
            {
                Token: token
            }
        );

        if (response.errors?.any()) {
            console.error(response.errors);
            return false;
        }

        return response.data.IsValid;
    }

    /**
     * Refresh a PIN frequency token.
     * @param documentType The document type being signed.
     * @param pin The user's PIN.
     * @returns The PIN frequency option and refreshed token if valid, else null.
     */
    public static async refreshPinFrequencyToken(documentType: PinFrequencyDocumentType, pin: string): Promise<IRefreshSignaturePinTokenResponse> {
        const response = await Client.postAsync<IRefreshSignaturePinTokenResponse>(
            ServerOperations.Server.Myle_Settings_RefreshSignaturePinToken,
            {
                SignaturePinType: documentType,
                Pin: pin
            }
        );

        if (response.errors?.any()) {
            console.error(response.errors);
            return null;
        }

        return response.data;
    }
}

export { SignatureClient };
